import { useCallback, useEffect, useMemo, useState } from "react";
import { formatShortDate } from "@/lib/date";
import { toast } from "sonner";
import { useHousehold } from "@/context/HouseholdContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ObligationCard from "@/components/obligations/ObligationCard";
import CandidateTrayCard from "@/components/obligations/CandidateTrayCard";
import CaptureBox from "@/components/obligations/CaptureBox";
import ProofTrailPopover from "@/components/obligations/ProofTrailPopover";
import EvidenceDrawer from "@/components/obligations/EvidenceDrawer";
import QuarantineRail from "@/components/obligations/QuarantineRail";
import {
  acceptCandidate,
  actionObligation,
  captureObligation,
  confirmCapture,
  confirmSuggestion,
  dismissCandidate,
  getObligationEvents,
  listCandidates,
  listObligations,
  listSuggestions,
  normalizeObligation,
  obligationActionLabel,
  obligationCounts,
  partitionCandidates,
  promoteCandidate,
  rejectSuggestion,
} from "@/lib/obligationsApi";
import { CheckCircle, ClockCountdown, Sparkle, WarningCircle } from "@phosphor-icons/react";

const laneMeta = {
  needs_you: { title: "Needs you", empty: "Nothing is waiting on your hand here." },
  waiting_on_them: { title: "Waiting on them", empty: "No open follow-through is outstanding." },
  gated: { title: "Gated", empty: "Nothing is held behind a date right now." },
};

const statusBadge = {
  positive: "bg-[#e7efe6] text-[#4f7a5b]",
  attention: "bg-[#f5ecd9] text-[#9a6f2b]",
};

const formatHeadlineDate = () =>
  new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  }).format(new Date());

const formatMoney = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const number = Number(value);
  if (Number.isNaN(number)) return null;
  return number.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: Number.isInteger(number) ? 0 : 2 });
};

const formatTimestamp = (value) => {
  if (!value) return "now";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const reviewIcon = (kind) => {
  if (kind === "suggestion") return CheckCircle;
  return Sparkle;
};

const placeholderOpportunities = [
  { id: "opp-1", title: "Opportunities will land here", detail: "This rail is reserved for future surfaced opportunities." },
];

export default function ObligationsDesk() {
  const { hid, ownerLabel } = useHousehold();
  const [tab, setTab] = useState("queue");
  const [loading, setLoading] = useState(true);
  const [captureBusy, setCaptureBusy] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [desk, setDesk] = useState({ obligations: [], captureReview: [], opportunities: [], filedTodayCount: 0, filedTodayNote: "" });
  const [suggestions, setSuggestions] = useState([]);
  const [candidateLanes, setCandidateLanes] = useState({ desk: [], quarantine: [] });
  const [busyActionId, setBusyActionId] = useState("");
  const [proofFor, setProofFor] = useState(null);
  const [proofEvents, setProofEvents] = useState([]);
  const [drawerFor, setDrawerFor] = useState(null);

  const load = useCallback(async () => {
    if (!hid) return;
    setLoading(true);
    try {
      const [deskData, suggestionData, candidateData] = await Promise.all([
        listObligations(hid, showAudit ? { audit: "1" } : {}),
        listSuggestions(hid),
        listCandidates(hid, { lane: "all" }),
      ]);
      setDesk(deskData);
      setSuggestions(suggestionData);
      setCandidateLanes(partitionCandidates(candidateData));
    } catch (error) {
      toast.error("Could not load obligations desk");
    } finally {
      setLoading(false);
    }
  }, [hid, showAudit]);

  useEffect(() => {
    load();
  }, [load]);

  const deskCandidates = useMemo(() => candidateLanes.desk || [], [candidateLanes.desk]);
  const quarantineCandidates = useMemo(() => candidateLanes.quarantine || [], [candidateLanes.quarantine]);
  const reviewItems = useMemo(() => [...suggestions, ...(desk.captureReview || [])], [suggestions, desk.captureReview]);
  const reviewQueue = useMemo(() => [...deskCandidates, ...quarantineCandidates, ...reviewItems], [deskCandidates, quarantineCandidates, reviewItems]);
  const counts = useMemo(() => obligationCounts(desk.obligations, reviewQueue), [desk.obligations, reviewQueue]);

  const lanes = useMemo(() => {
    const grouped = { needs_you: [], waiting_on_them: [], gated: [] };
    for (const obligation of desk.obligations) {
      if (grouped[obligation.lane]) grouped[obligation.lane].push(obligation);
    }
    return grouped;
  }, [desk.obligations]);

  const auditRows = useMemo(
    () => desk.obligations.filter((obligation) => ["voided", "merged", "handled"].includes(obligation.lane)),
    [desk.obligations]
  );

  const mergeCandidates = useMemo(
    () =>
      desk.obligations.filter(
        (obligation) => !["merged", "voided"].includes(obligation.lane)
      ),
    [desk.obligations]
  );

  const openProof = async (obligation) => {
    setProofFor(obligation);
    setProofEvents(obligation.events || []);
    try {
      const extraEvents = await getObligationEvents(hid, obligation.id);
      setProofEvents(extraEvents);
    } catch {
      // Best effort; the row already carries enough fallback history for the desk.
    }
  };

  const openEvidence = (obligation) => {
    if (!obligation) return;
    setDrawerFor(obligation);
    if (!proofFor || proofFor.id !== obligation.id) {
      setProofFor(obligation);
      setProofEvents(obligation.events || []);
    }
  };

  const runAction = async (obligation, action, payload = {}) => {
    setBusyActionId(`${obligation.id}:${action}`);
    try {
      await actionObligation(hid, obligation.id, action, payload);
      if (proofFor?.id === obligation.id) {
        const eventText = payload?.gate_date ? `Until ${payload.gate_date}` : `${action} recorded`;
        setProofEvents((current) => [
          ...current,
          {
            id: `${obligation.id}-${action}-${Date.now()}`,
            type: obligationActionLabel(action),
            icon: action,
            timestamp: new Date().toISOString(),
            snippet: eventText,
            payload: { action, ...payload },
          },
        ]);
      }
      await load();
      return true;
    } catch (error) {
      toast.error("Action did not stick");
      return false;
    } finally {
      setBusyActionId("");
    }
  };

  const submitCapture = async (text) => {
    setCaptureBusy(true);
    try {
      const captureItems = await captureObligation(hid, text);
      setDesk((current) => ({
        ...current,
        captureReview: [...captureItems, ...(current.captureReview || [])],
      }));
      toast.success("Capture ready for confirmation");
      return true;
    } catch (error) {
      toast.error("Capture could not be created");
      return false;
    } finally {
      setCaptureBusy(false);
    }
  };

  const confirmReviewItem = async (item) => {
    try {
      if (item.kind === "suggestion") {
        await confirmSuggestion(hid, item);
      } else {
        await confirmCapture(hid, item);
      }
      await load();
    } catch (error) {
      toast.error("Confirmation failed");
    }
  };

  const acceptTrayCandidate = async (candidate, overrides = {}) => {
    const busyId = `${candidate.id}:candidate-accept`;
    setBusyActionId(busyId);
    try {
      const created = await acceptCandidate(hid, candidate, overrides);
      setCandidateLanes((current) => ({
        ...current,
        desk: (current.desk || []).filter((entry) => entry.id !== candidate.id),
      }));
      setDesk((current) => ({
        ...current,
        obligations: [created, ...current.obligations.filter((entry) => entry.id !== created.id)],
      }));
    } catch (error) {
      if (error?.response?.status === 404) {
        const refreshed = await listCandidates(hid, { lane: "all" });
        setCandidateLanes(partitionCandidates(refreshed));
        toast.error("Candidate already resolved elsewhere");
      } else {
        toast.error("Candidate could not be accepted");
      }
    } finally {
      setBusyActionId("");
    }
  };

  const dismissTrayCandidate = async (candidate) => {
    const busyId = `${candidate.id}:candidate-dismiss`;
    setBusyActionId(busyId);
    try {
      await dismissCandidate(hid, candidate);
      setCandidateLanes((current) => ({
        desk: (current.desk || []).filter((entry) => entry.id !== candidate.id),
        quarantine: (current.quarantine || []).filter((entry) => entry.id !== candidate.id),
      }));
    } catch (error) {
      toast.error("Candidate could not be dismissed");
    } finally {
      setBusyActionId("");
    }
  };

  const promoteQuarantineCandidate = async (candidateId) => {
    const candidate = quarantineCandidates.find((entry) => entry.id === candidateId);
    if (!candidate) return;
    const busyId = `${candidateId}:candidate-promote`;
    setBusyActionId(busyId);
    try {
      await promoteCandidate(hid, candidateId);
      toast.success(`Admitted ${candidate.senderEmail || "sender"} and promoted "${candidate.title}" to Needs you`);
      await load();
    } catch (error) {
      toast.error("Sender was not admitted and promoted");
    } finally {
      setBusyActionId("");
    }
  };

  const dismissReviewItem = async (item) => {
    try {
      if (item.kind === "suggestion") {
        await rejectSuggestion(hid, item);
      } else {
        setDesk((current) => ({
          ...current,
          captureReview: (current.captureReview || []).filter((candidate) => candidate.id !== item.id),
        }));
      }
      await load();
    } catch (error) {
      toast.error("Could not dismiss that review item");
    }
  };

  const reviewCards = reviewItems;
  const opportunities = desk.opportunities?.length ? desk.opportunities : placeholderOpportunities;
  const nothingNeedsYou =
    counts.needs_you === 0 &&
    counts.waiting_on_them === 0 &&
    counts.gated === 0 &&
    reviewCards.length === 0 &&
    deskCandidates.length === 0 &&
    quarantineCandidates.length === 0;
  const busyActionFor = (obligationId) => (busyActionId.startsWith(`${obligationId}:`) ? busyActionId.split(":")[1] : "");

  return (
    <div className="relative pb-36">
      <section className="mb-6 flex flex-wrap items-center gap-4 rounded-[1rem] border border-[hsl(var(--border))] bg-card px-5 py-4">
        <p className="font-serif text-[1.03rem] text-[hsl(var(--foreground))]">
          Your desk, {formatHeadlineDate()} — <b className="border-b-2 border-[hsl(var(--accent))] font-medium">{counts.needs_you} need you</b> · {counts.gated} gated · {counts.to_confirm} to ratify
        </p>
        <div className="ml-auto flex flex-wrap gap-3 text-sm text-[hsl(var(--muted-foreground))]">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#f5ecd9] px-3 py-1 text-[11.5px] text-[#9a6f2b]">
            <WarningCircle size={12} weight="fill" />
            {counts.needs_you} needs you
          </span>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#ecebe6] px-3 py-1 text-[11.5px] text-[#6b7384]">
            <ClockCountdown size={12} weight="fill" />
            {counts.waiting_on_them} waiting
          </span>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#e7efe6] px-3 py-1 text-[11.5px] text-[#4f7a5b]">
            <CheckCircle size={12} weight="fill" />
            {counts.to_confirm} to confirm
          </span>
        </div>
      </section>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="mb-4 flex items-center justify-between gap-4">
          <TabsList className="h-auto rounded-none bg-transparent p-0 text-[hsl(var(--muted-foreground))]">
            <TabsTrigger value="queue" className="rounded-none border-b-2 border-transparent px-0 pb-1.5 pt-0 text-[13px] data-[state=active]:border-[hsl(var(--accent))] data-[state=active]:bg-transparent data-[state=active]:shadow-none">
              Queue
            </TabsTrigger>
            <TabsTrigger value="filed" className="rounded-none border-b-2 border-transparent px-0 pb-1.5 pt-0 text-[13px] data-[state=active]:border-[hsl(var(--accent))] data-[state=active]:bg-transparent data-[state=active]:shadow-none">
              Filed facts
            </TabsTrigger>
            <TabsTrigger value="opportunities" className="rounded-none border-b-2 border-transparent px-0 pb-1.5 pt-0 text-[13px] data-[state=active]:border-[hsl(var(--accent))] data-[state=active]:bg-transparent data-[state=active]:shadow-none">
              Opportunities
            </TabsTrigger>
          </TabsList>

          {tab === "queue" ? (
            <label className="inline-flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
              <input
                type="checkbox"
                checked={showAudit}
                onChange={(event) => setShowAudit(event.target.checked)}
                className="h-4 w-4 rounded border-[hsl(var(--input))] text-[hsl(var(--accent))] focus:ring-[hsl(var(--ring))]"
              />
              Show audit rows
            </label>
          ) : null}
        </div>

        <TabsContent value="queue" className="mt-0">
          <div className="flex flex-col gap-7 xl:flex-row xl:items-start">
            <div className="min-w-0 flex-1">
              <section className="mb-6">
                <h2 className="mb-3 text-[11px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">For your review</h2>
                {deskCandidates.length || reviewCards.length ? (
                  <div className="space-y-5">
                    {deskCandidates.length ? (
                      <section>
                        <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">
                          Feeder candidates
                        </p>
                        <div className="grid gap-4 xl:grid-cols-2">
                          {deskCandidates.map((candidate) => (
                            <CandidateTrayCard
                              key={candidate.id}
                              candidate={candidate}
                              ownerLabel={ownerLabel}
                              busy={busyActionId === `${candidate.id}:candidate-accept` || busyActionId === `${candidate.id}:candidate-dismiss`}
                              onAccept={acceptTrayCandidate}
                              onDismiss={dismissTrayCandidate}
                            />
                          ))}
                        </div>
                      </section>
                    ) : null}

                    {reviewCards.length ? (
                      <section>
                        <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">
                          Matcher suggestions and captures
                        </p>
                        <div className="grid gap-4 xl:grid-cols-2">
                          {reviewCards.map((item) => {
                            const Icon = reviewIcon(item.kind);
                            return (
                              <article key={`${item.kind}-${item.id}`} className="rounded-[1rem] border border-[hsl(var(--border))] bg-card px-4 py-4" data-testid={`review-card-${item.kind}-${item.id}`}>
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] ${statusBadge[item.kind === "suggestion" ? "positive" : "attention"]}`}>
                                    <Icon size={12} weight="fill" />
                                    {item.kind === "suggestion" ? "Suggested discharge" : "Parsed from your note"}
                                  </span>
                                  <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] ${item.ownerParty === "joint" ? "border-[hsl(var(--accent))] text-[#8a6d3f]" : "border-[hsl(var(--input))] text-[hsl(var(--muted-foreground))]"}`}>
                                    {item.ownerLabel || ownerLabel(item.ownerParty)}
                                  </span>
                                </div>
                                <h3 className="mt-3 font-serif text-[0.95rem] text-[hsl(var(--foreground))]">{item.title}</h3>
                                <p className="mt-2 text-[12.5px] text-[hsl(var(--muted-foreground))]">
                                  {[item.counterparty, item.asset, item.dueDate ? `Due ${formatShortDate(item.dueDate)}` : null].filter(Boolean).join(" · ")}
                                </p>
                                {item.amount ? <p className="mt-2 font-mono tabular-nums text-sm text-[hsl(var(--foreground))]">{formatMoney(item.amount)}</p> : null}
                                <div className="mt-4 flex flex-wrap gap-2">
                                  <Button type="button" className="rounded-[0.625rem]" onClick={() => confirmReviewItem(item)}>
                                    Confirm
                                  </Button>
                                  <Button type="button" variant="outline" className="rounded-[0.625rem] border-[hsl(var(--input))]" onClick={() => dismissReviewItem(item)}>
                                    Not this
                                  </Button>
                                </div>
                                <div className="mt-4 flex items-center gap-2 border-t border-[hsl(var(--border))] pt-3 text-[11.5px] text-[hsl(var(--muted-foreground))]">
                                  <Sparkle size={13} className="text-[hsl(var(--accent))]" />
                                  <span className="truncate">
                                    {item.provenance?.origin || "Your capture"} · {formatTimestamp(item.provenance?.timestamp)}
                                  </span>
                                  <button
                                    type="button"
                                    className="ml-auto font-medium text-[hsl(var(--foreground))] underline decoration-[hsl(var(--accent))] underline-offset-4"
                                    onClick={() => openEvidence(normalizeObligation({ ...item.raw, id: item.id, raw_text: item.rawText, source: item.provenance, title: item.title }))}
                                  >
                                    view
                                  </button>
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      </section>
                    ) : null}
                  </div>
                ) : (
                  <div className="rounded-[1rem] border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/60 px-5 py-5 text-sm text-[hsl(var(--muted-foreground))]">
                    Nothing waiting for confirmation right now.
                  </div>
                )}
              </section>

              {loading ? (
                <div className="rounded-[1rem] border border-[hsl(var(--border))] bg-card px-5 py-10 text-sm text-[hsl(var(--muted-foreground))]">
                  Loading obligations desk…
                </div>
              ) : nothingNeedsYou ? (
                <div className="rounded-[1rem] border border-[hsl(var(--border))] bg-card px-8 py-12 text-center">
                  <p className="font-serif text-[1.7rem] text-[hsl(var(--foreground))]">Nothing needs you today.</p>
                  <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-[hsl(var(--muted-foreground))]">
                    Quiet work can stay quiet. The desk will surface the next real obligation when it is ready for your hand.
                  </p>
                </div>
              ) : (
                <div className="grid gap-4 xl:grid-cols-3">
                  {Object.entries(laneMeta).map(([laneKey, meta]) => (
                    <section key={laneKey} className="min-w-0">
                      <h3 className="mb-3 font-serif text-[0.98rem] text-[hsl(var(--foreground))]">
                        {meta.title} <span className="font-sans text-xs text-[hsl(var(--muted-foreground))]">({lanes[laneKey].length})</span>
                      </h3>
                      {lanes[laneKey].length ? (
                        <div className="space-y-3">
                          {lanes[laneKey].map((obligation) => (
                            <ObligationCard
                              key={obligation.id}
                              obligation={obligation}
                              mergeCandidates={mergeCandidates.filter((candidate) => candidate.id !== obligation.id)}
                              ownerLabel={ownerLabel}
                              onOpenEvidence={openEvidence}
                              onOpenProof={openProof}
                              onAction={runAction}
                              busyAction={busyActionFor(obligation.id)}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="rounded-[1rem] border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/50 px-4 py-5 text-sm text-[hsl(var(--muted-foreground))]">
                          {meta.empty}
                        </div>
                      )}
                    </section>
                  ))}
                </div>
              )}

                {showAudit && auditRows.length ? (
                  <section className="mt-6">
                    <h3 className="mb-3 text-[11px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">Audit / archive</h3>
                  <div className="space-y-3">
                    {auditRows.map((obligation) => (
                      <ObligationCard
                        key={`audit-${obligation.id}`}
                        obligation={obligation}
                        mergeCandidates={mergeCandidates.filter((candidate) => candidate.id !== obligation.id)}
                        ownerLabel={ownerLabel}
                        onOpenEvidence={openEvidence}
                        onOpenProof={openProof}
                        onAction={runAction}
                        busyAction={busyActionFor(obligation.id)}
                      />
                    ))}
                  </div>
                  </section>
                ) : null}

              <QuarantineRail
                candidates={quarantineCandidates}
                busyId={busyActionId}
                onPromote={promoteQuarantineCandidate}
                onDismiss={(candidateId) => dismissTrayCandidate({ id: candidateId })}
              />
            </div>

            <aside className="w-full shrink-0 xl:w-[16.4rem]">
              <h2 className="mb-3 text-[11px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">Opportunities</h2>
              <div className="space-y-3">
                {opportunities.map((opportunity) => (
                  <article key={opportunity.id} className="rounded-[1rem] border border-[hsl(var(--border))] bg-card px-4 py-4">
                    <h3 className="font-serif text-[0.9rem] text-[hsl(var(--foreground))]">{opportunity.title}</h3>
                    <p className="mt-2 text-[11.5px] text-[hsl(var(--muted-foreground))]">{opportunity.detail || opportunity.subtitle}</p>
                    <div className="mt-3 flex items-center gap-2 text-[11.5px] text-[hsl(var(--muted-foreground))]">
                      <Sparkle size={13} className="text-[hsl(var(--accent))]" />
                      <span>Placeholder for a later round</span>
                    </div>
                  </article>
                ))}
              </div>

              <h2 className="mb-3 mt-6 text-[11px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">Filed quietly today</h2>
              <article className="rounded-[1rem] border border-[hsl(var(--border))] bg-card px-4 py-4 text-[11.5px] leading-6 text-[hsl(var(--muted-foreground))]">
                {desk.filedTodayNote || (desk.filedTodayCount ? `${desk.filedTodayCount} facts filed quietly today. Nothing needed you.` : "Nothing filed quietly today yet.")}
              </article>
            </aside>
          </div>
        </TabsContent>

        <TabsContent value="filed" className="mt-0">
          <div className="rounded-[1rem] border border-[hsl(var(--border))] bg-card px-6 py-8">
            <p className="font-serif text-[1.15rem] text-[hsl(var(--foreground))]">Filed facts</p>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">A quiet placeholder for the later filed-facts round.</p>
          </div>
        </TabsContent>

        <TabsContent value="opportunities" className="mt-0">
          <div className="rounded-[1rem] border border-[hsl(var(--border))] bg-card px-6 py-8">
            <p className="font-serif text-[1.15rem] text-[hsl(var(--foreground))]">Opportunities</p>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">This round reserves the surface; decision support lands here later.</p>
          </div>
        </TabsContent>
      </Tabs>

      <CaptureBox onCapture={submitCapture} loading={captureBusy} />

      <ProofTrailPopover
        open={!!proofFor}
        obligation={proofFor}
        events={proofEvents}
        onClose={() => setProofFor(null)}
        onOpenEvidence={(obligation) => openEvidence(obligation || proofFor)}
      />

      <EvidenceDrawer
        open={!!drawerFor}
        obligation={drawerFor}
        events={drawerFor?.id && drawerFor.id === proofFor?.id && proofEvents.length ? proofEvents : (drawerFor?.events || [])}
        onOpenChange={(open) => {
          if (!open) setDrawerFor(null);
        }}
      />
    </div>
  );
}
